import React, { useState, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Box, Button, FormField, Text } from '@indeed/ifl-components';
import PhoneInputField from '@indeed/ipl-phone-input-field';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import EmailAddressField from '../../../components/EmailAddressField/EmailAddressField';
import {
    useGetContactDetailsFormDataQuery,
    useUpdateLightApplyContactDetailsMutation,
} from './__generated__/ContactDetailsPage.generated';
import {
    updateSmartApplyContactInfoCache,
    buildCompleteContactInfoMutationInput,
    validateCacheServerConsistency,
    SmartApplyContactInfoUpdate,
} from '../../../lib/apollo/smartApplyStateCache';
import PageTransition from '../../../components/PageTransition/PageTransition';
import FormHeader from '../components/FormHeader/FormHeader';
import { NodeIdContext } from '../../../lib/context/NodeIdContext';
import { useSearchParams } from 'react-router-dom';

/**
 * ContactDetailsPage with Direct SmartApplyApplicationState Cache Updates
 *
 * This approach:
 * 1. ✅ Writes directly to SmartApplyApplicationState cache (no separate local state)
 * 2. ✅ Handles phoneNumber and email fields
 * 3. ✅ Single server mutation on "Continue" (bulk save)
 * 4. ✅ Zero useState complexity for Apollo data
 * 5. ✅ Uses OneGraph schema as the single source of truth
 */
const ContactDetailsPage: React.FC = () => {
    const { goForward, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();

    const [searchParams] = useSearchParams();
    const indeedApplyableJobId = searchParams.get('indeedApplyableJobId') || '';
    // Get nodeId from context
    const nodeId = useContext(NodeIdContext);

    // 1. Get server data (SmartApply + JobSeeker profile + ApplyParameters contact config) - Apollo should auto-update on cache changes
    const { data, loading } = useGetContactDetailsFormDataQuery({
        variables: {
            nodeId: nodeId || '', // Provide empty string as fallback for TypeScript
            indeedApplyableJobId, // Pass job ID to get contact configuration from applyParameters
        },
        skip: !nodeId || !indeedApplyableJobId, // Skip query when either parameter is missing
        errorPolicy: 'all',
        // Cache-first: return cache data immediately, only fetch if not in cache
        fetchPolicy: 'cache-first',
    });

    // 2. Server mutation for bulk save on Continue
    const [updateServerContactDetails, { loading: saving }] =
        useUpdateLightApplyContactDetailsMutation();

    // 3. Extract current state from Apollo data - should auto-update when data changes
    const smartApplyState =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    const contactInfo = smartApplyState?.contactInfo;

    // For contact details, assume phone is required and email is always required
    const isPhoneRequired = true; // Default behavior for contact info
    const isEmailRequired = true; // Email is typically always required

    // 4. Handle form field changes with direct cache writes - Apollo should auto-update
    const handleFieldChange = (field: keyof SmartApplyContactInfoUpdate, value: string) => {
        // Prevent cache updates while data is still loading
        if (loading) {
            return;
        }

        if (!smartApplyState?.id) {
            console.warn('No SmartApplyApplicationState ID available for cache update');
            return;
        }

        // Use cache.modify() - more flexible, no fragment validation issues
        const updatedContactInfo = updateSmartApplyContactInfoCache(
            apolloClient.cache,
            smartApplyState.id,
            { [field]: value },
        );

        if (!updatedContactInfo) {
            console.error(`Failed to update ${field}`);
        }
    };

    // State to track when user has attempted to continue (for validation UX)
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handlePhoneBlur = (params: any) => {
        const phoneValue = params.fullValue || '';

        // Update cache immediately (single source of truth)
        handleFieldChange('phoneNumber', phoneValue);
    };

    // 5. Handle Continue - flush all changes to server
    const handleContinue = async () => {
        if (!smartApplyState?.id || !contactInfo) {
            console.error('Cannot save: missing SmartApplyApplicationState ID or contact info');
            return;
        }

        // Validation - require phone and email based on standard behavior
        const hasRequiredFields = !!(
            (!isPhoneRequired || contactInfo.phoneNumber?.trim()) &&
            (!isEmailRequired || contactInfo.email?.trim())
        );

        if (!hasRequiredFields) {
            // Show validation errors now that user has attempted to continue
            setHasAttemptedContinue(true);
            return;
        }

        // ✅ CRITICAL: Build complete mutation input to prevent server data loss
        // The server does NOT support partial updates - must include ALL contactInfo fields
        const completeMutationInput = buildCompleteContactInfoMutationInput(
            contactInfo, // Complete current contactInfo from cache
            {
                // Only the fields we want to update (but all existing fields preserved)
                phoneNumber: contactInfo.phoneNumber,
                email: contactInfo.email,
            },
        );

        const mutationVariables = {
            input: { id: smartApplyState.id },
            updateContactInfoInput: completeMutationInput,
        };

        try {
            // Send complete contactInfo to server (all fields included)
            await updateServerContactDetails({
                variables: mutationVariables,
                // ✅ Validate that server preserved all data
                update: (cache, { data: mutationData }) => {
                    if (mutationData?.smartApplyApplicationState?.updateContactInfo?.state) {
                        const serverContactInfo =
                            mutationData.smartApplyApplicationState.updateContactInfo.state;

                        // ✅ Validate that server returned ALL fields we sent (data preservation check)
                        validateCacheServerConsistency(
                            completeMutationInput,
                            serverContactInfo,
                            'UpdateLightApplyContactDetails',
                        );

                        console.log(
                            '✅ Contact details successfully saved with complete data preservation',
                        );
                        void goForward();
                    }
                },
            });
        } catch (error) {
            console.error('Failed to save contact details:', error);
            // Could show error message to user
        }
    };

    // 6. Form validation and dirty state from cache
    const canContinue = !!(
        (!isPhoneRequired || contactInfo?.phoneNumber?.trim()) &&
        (!isEmailRequired || contactInfo?.email?.trim())
    );

    if (loading) {
        return (
            <PageTransition pageKey="contact-info-details">
                <Helmet>
                    <title>連絡先を入力してください - Light Apply - Indeed</title>
                </Helmet>

                <Box sx={{ padding: '32px 16px', textAlign: 'center' }}>
                    <Text>プロフィール情報を読み込んでいます...</Text>
                </Box>
            </PageTransition>
        );
    }

    return (
        <PageTransition pageKey="contact-info-details">
            <Helmet>
                <title>連絡先を入力してください - Light Apply - Indeed</title>
            </Helmet>

            <Box sx={{ inlineSize: '100%' }}>
                <FormHeader step={2} total={5} title="連絡先を入力してください" />

                {/* Form Fields - Reading from SmartApplyApplicationState cache */}
                <Box
                    sx={{
                        marginBlockEnd: '32px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    <Box sx={{ marginBlockEnd: '24px', inlineSize: '100%' }}>
                        {/* Phone Number Input */}
                        <Box sx={{ marginBlockEnd: '24px' }}>
                            <FormField
                                errorText={
                                    hasAttemptedContinue &&
                                    isPhoneRequired &&
                                    !contactInfo?.phoneNumber?.trim()
                                        ? '電話番号は必須です'
                                        : ''
                                }
                                invalid={
                                    hasAttemptedContinue &&
                                    isPhoneRequired &&
                                    !contactInfo?.phoneNumber?.trim()
                                }
                            >
                                <PhoneInputField
                                    label="電話番号"
                                    showRequiredIndicator={isPhoneRequired}
                                    defaultValue={contactInfo?.phoneNumber || ''}
                                    defaultCountry="JP"
                                    locale="ja-JP"
                                    onBlur={handlePhoneBlur}
                                    helperText="応募先または送信先の採用企業にのみ公開します。"
                                    a11yStrings={{
                                        countrySelectAriaLabel: '国を選択',
                                        inputAriaLabel: '電話番号を入力',
                                    }}
                                    inputProps={{
                                        placeholder: '例）0912345678',
                                    }}
                                />
                            </FormField>
                        </Box>

                        {/* Email Display */}
                        <EmailAddressField
                            label="メールアドレス"
                            value={contactInfo?.email || 'testemail@xyz.com'}
                            onInfoClick={() => {
                                /* Handle email info click */
                            }}
                        />
                    </Box>

                    {/* Continue Button - Bulk Save to Server */}
                    <Box>
                        <Button
                            variant="primary"
                            size="lg"
                            sx={{ inlineSize: '100%' }}
                            onClick={handleContinue}
                            disabled={!canContinue || saving || pageFlowLoading}
                        >
                            {saving ? '保存中...' : pageFlowLoading ? '読み込み中...' : '次へ'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ContactDetailsPage;
